import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { useLocale, useTranslations } from 'next-intl';

import { HELP_COMMAND, ICommandDefinition, UNA_FLEX_COMMAND } from './commands';

interface ITerminalContext {
  consoleHistory: Array<[prefix: string, Message: FunctionComponent]>;
  activeCommands: Array<ICommandDefinition>;
  processCommand(command: ICommandDefinition): void;
}
const TerminalContext = createContext<ITerminalContext>({
  consoleHistory: [],
  activeCommands: [],
  processCommand() {},
});

export function TerminalProvider({ children }: PropsWithChildren) {
  const t = useTranslations();
  const locale = useLocale();
  const [prevLocale, setPrevLocale] = useState(locale);
  const [consoleHistory, setConsoleHistory] = useState<
    ITerminalContext['consoleHistory']
  >([]);

  if (prevLocale !== locale) {
    setPrevLocale(locale);
    setConsoleHistory([]);
  }

  const [activeCommands, setActiveCommands] = useState<
    ITerminalContext['activeCommands']
  >([UNA_FLEX_COMMAND, HELP_COMMAND]);

  const processCommand = useCallback<(command: ICommandDefinition) => void>(
    ({
      message,
      userInput,
      response,
      commands = [HELP_COMMAND],
      externalUrl,
    }) => {
      if (externalUrl) {
        window.open(externalUrl, '_blank', 'noopener,noreferrer');
      }

      setConsoleHistory((current) => {
        const messages = [...current];
        messages.push([
          'P:',
          () =>
            userInput
              ? t(userInput)
              : t.rich(message, {
                  button: (chunks) => chunks,
                }),
        ]);
        if (response) messages.push(['', () => t.rich(...response)]);

        return messages;
      });

      if (!externalUrl) {
        const filtered = commands.filter(
          (c) => c.message !== UNA_FLEX_COMMAND.message,
        );
        setActiveCommands([UNA_FLEX_COMMAND, ...filtered]);
      }
    },
    [locale],
  );

  return (
    <TerminalContext.Provider
      value={{
        consoleHistory,
        activeCommands,
        processCommand,
      }}
    >
      {children}
    </TerminalContext.Provider>
  );
}

export const useTerminal = () => useContext(TerminalContext);
